import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getWriteReviewUrl, getConsumerReviewsUrl, getCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { Link } from 'site-modules/shared/components/link/link';
import { getPublicationStateForMakeModel, isUsed } from 'site-modules/shared/utils/publication-states';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { PhotoflipperButtonAsync } from 'site-modules/shared/components/photoflipper-core/photoflipper-button/photoflipper-button-async';
import Button from 'reactstrap/lib/Button';

const LINK_PROPS = {
  params: PropTypes.shape({
    makeSlug: PropTypes.string,
    modelSlug: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  mmsy: PropTypes.shape({
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    make: PropTypes.string,
    model: PropTypes.string,
    submodel: PropTypes.string,
  }),
  classes: PropTypes.string,
  target: PropTypes.string,
  actionButtonsEnabled: PropTypes.bool,
  showArrowIcon: PropTypes.bool,
};

/**
 * Write review link (`/bmw/x3/2018/write-review.html`).
 */
export function WriteReview({
  params,
  mmsy = {},
  classes = 'text-primary-darker',
  showArrowIcon = true,
  target = '_self',
  ...otherProps
}) {
  return (
    <Link
      to={getWriteReviewUrl(params)}
      className={classes}
      rel="nofollow"
      data-tracking-id="write_review"
      data-tracking-value={`Write a ${mmsy.year} ${mmsy.make} ${mmsy.model} review`}
      {...otherProps}

      target={target}
    >
      Write a vehicle review
      {showArrowIcon && <i className="icon-arrow-right4 size-10 ps-0_25" aria-hidden />}
    </Link>
  );
}

WriteReview.propTypes = LINK_PROPS;

/**
 * See all consumer reviews link (`/bmw/x3/2018/consumer-reviews/`).
 */
export function SeeAllReviewsLink({
  params,
  reviewsCount = 0,
  classes = 'text-primary-darker',
  rel = null,
  target = '_self',
  mmsy = {},
  displayVehicle = null,
  showArrowIcon = true,
}) {
  return (
    <Link
      to={getConsumerReviewsUrl(params)}
      className={classnames('see-all-reviews', classes)}
      data-tracking-id="read_all_reviews"
      data-tracking-value="Read All Reviews"
      rel={rel}
      target={target}
    >
      {`See all ${reviewsCount} reviews${
        displayVehicle || (mmsy && mmsy.model) ? ` of the ${displayVehicle || `${mmsy.make} ${mmsy.model}`}` : ''
      }`}
      {showArrowIcon && <i className="icon-arrow-right4 size-10 ps-0_25" aria-hidden />}
    </Link>
  );
}

SeeAllReviewsLink.propTypes = {
  ...LINK_PROPS,
  displayVehicle: PropTypes.string,
  rel: PropTypes.string,
  reviewsCount: PropTypes.number,
};

export function ShopAllForSaleLink({
  params,
  mmsy = {},
  classes = 'text-primary-darker',
  pubStates = {},
  trackingId = null,
  target = '_self',
}) {
  return (
    <Link
      className={classnames('see-all-for-sale-link', classes)}
      to={
        isUsed(pubStates)
          ? getCoreUrl({ makeSlug: params.makeSlug, modelSlug: params.modelSlug, year: params.year })
          : srpLinkBuilder({
              make: mmsy.make,
              model: mmsy.model,
              submodel: mmsy.submodel,
              year: mmsy.year,
              inventorytype: getPublicationStateForMakeModel(pubStates),
            })
      }
      data-tracking-id={trackingId}
      target={target}
      rel="nofollow"
    >
      <span className="text-normal fw-normal size-16">
        Shop {mmsy.model} {mmsy.year} for sale
      </span>
      <i className="icon-arrow-right3 d-inline-block size-10 ms-0_25" aria-hidden />
    </Link>
  );
}

ShopAllForSaleLink.propTypes = {
  ...LINK_PROPS,
  pubStates: PropTypes.shape({}),
  trackingId: PropTypes.string,
};

export function PhotoflipperButton({ params, classes = 'text-primary-darker' }) {
  return (
    <PhotoflipperButtonAsync makeSlug={params.makeSlug} modelSlug={params.modelSlug} year={params.year}>
      <Button
        color="link"
        className={classnames('p-a-v-link p-0 size-16 text-transform-none fw-normal border-0', classes)}
      >
        View Photos &&nbsp;Videos
        <i className="icon-arrow-right4 d-inline-block size-10 ps-0_25" aria-hidden />
      </Button>
    </PhotoflipperButtonAsync>
  );
}

PhotoflipperButton.propTypes = {
  ...LINK_PROPS,
};

/**
 * Report abuse comment link (`//help.edmunds.com/hc/en-us/requests/new?{tickedId};{ticketSubject};{ticketDescription`).
 */
export function ReportAbuseLink({ reviewTitle, reviewId, userName, classes = '' }) {
  const ticketFormId = 'ticket_form_id=2343&amp';
  const ticketSubject = `ticket[subject]=Report review (${reviewId}) to Edmunds.com for invistigation&amp`;
  const ticketDescription = `ticket[description]=I am reporting this review ${reviewTitle} posted by ${userName} because...`;
  return (
    <Link
      to={`//help.edmunds.com/hc/en-us/requests/new?${ticketFormId};${ticketSubject};${ticketDescription}`}
      className={classnames('d-inline-block text-gray-darker', classes)}
      rel="nofollow"
    >
      Report abuse
      <i className="icon-arrow-right4 size-10 ps-0_25" aria-hidden />
    </Link>
  );
}

ReportAbuseLink.propTypes = {
  reviewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  reviewTitle: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  classes: PropTypes.string,
};
